import React, { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Modal } from '../ui/Modal'
import { X, Calendar } from 'lucide-react'

interface ProjectSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: { title: string; due_date: string | null }) => Promise<void>
  project: {
    id: string
    title: string
    due_date?: string
  }
  loading?: boolean
}

export const ProjectSettingsModal: React.FC<ProjectSettingsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  project,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    due_date: '',
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  // Initialize form data when modal opens or project changes
  useEffect(() => {
    if (isOpen && project) {
      setFormData({
        title: project.title || '',
        due_date: project.due_date ? project.due_date.split('T')[0] : '', // Extract date part only
      })
      setErrors({})
    }
  }, [isOpen, project])

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Project title is required'
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Project title must be at least 3 characters'
    } else if (formData.title.trim().length > 200) {
      newErrors.title = 'Project title must be less than 200 characters'
    }

    if (formData.due_date) {
      const dueDate = new Date(formData.due_date)
      const today = new Date()
      today.setHours(0, 0, 0, 0) // Reset time to start of day for comparison
      
      if (dueDate < today) {
        newErrors.due_date = 'Due date cannot be in the past'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      await onSave({
        title: formData.title.trim(),
        due_date: formData.due_date || null,
      })
      onClose()
    } catch (error) {
      console.error('Error saving project settings:', error)
    }
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
    }
  }

  // Layout mirrors UploadScopeModal (create-project popup): size="lg" modal
  // with built-in title bar, text-xs semibold labels, mb-4 field rhythm.
  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Project Settings" size="lg">
      <form onSubmit={handleSubmit}>
        {/* Project Title */}
        <div className="mb-4">
          <label className="text-xs font-semibold block mb-1">
            Project Title
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Enter project title"
            disabled={loading}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-brand-700 disabled:opacity-50"
          />
          {errors.title && (
            <p className="text-xs text-red-600 mt-1">{errors.title}</p>
          )}
        </div>

        {/* Due Date */}
        <div className="mb-4">
          <label className="text-xs font-semibold block mb-1">
            Due Date
          </label>
          <input
            type="date"
            value={formData.due_date}
            onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
            disabled={loading}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-brand-700 disabled:opacity-50"
          />
          {errors.due_date ? (
            <p className="text-xs text-red-600 mt-1">{errors.due_date}</p>
          ) : (
            <p className="text-xs text-slate-500 mt-1">
              Leave empty if no due date is required
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-2 mt-5 pt-3 border-t border-slate-100">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={loading}
            disabled={loading || !formData.title.trim()}
          >
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default ProjectSettingsModal